import {LocaleService} from '@frontend/services/LocaleService'
import type {
    LanguageCode,
    LanguageConfig,
    RegionCode,
    RegionConfig,
} from '@frontend/types/Locales'
import type {
    BasicLanguageConfig,
    BasicRegionConfig,
} from 'swayok-react-mdb-ui-kit/types/Locale'
import {create} from 'zustand/react'
import {setAppUrlsForRegion} from '../AppUrl'
import {configureAfterLocalizationLoaded} from '../configureAfterLocalizationLoaded'

interface InitReturn {
    language: LanguageConfig
    region: RegionConfig
}

// Тип состояния хранилища.
export interface LocaleStoreType {
    // Данные:

    // Текущий язык.
    language: LanguageConfig

    // Текущий регион.
    region: RegionConfig

    // Загружен ли словарь переводов?
    translationsLoaded: boolean

    // Счётчик для принудительного обновления корневого компонента.
    // Используется при обновлении переводов при переключенном HMR.
    forceRerender: number

    // API:

    // Инициализация языка и региона приложения.
    init: () => Promise<InitReturn>

    // Задание языка.
    loadLanguage: (
        language: LanguageConfig | BasicLanguageConfig | LanguageCode | string
    ) => Promise<void>

    // Применить предпочитаемый язык пользователя.
    applyPreferredUserLanguage: (language: LanguageCode) => void

    // Задание региона.
    setRegion: (
        region: RegionConfig | BasicRegionConfig | RegionCode | string
    ) => void
}

// Хранилище региона и языка приложения.
export const useLocaleStore = create<LocaleStoreType>(
    (set, get) => ({
        language: LocaleService.defaultLanguage,
        region: LocaleService.defaultRegion,
        translationsLoaded: false,
        forceRerender: 0,

        // API:

        async init(): Promise<InitReturn> {
            // Определение и загрузка языка.
            const language = LocaleService.detectLanguage()
            await get().loadLanguage(language)
            // Определение и загрузка региона.
            const region = LocaleService.detectRegion()
            get().setRegion(region)
            configureAfterLocalizationLoaded()
            // В режиме HMR переводы могут быть перезагружены при изменении строк.
            // В этом случае нужно принудительно перерисовать корневой компонент.
            LocaleService.onLanguageReload(
                'useLocaleStore',
                () => {
                    console.log('[useLocaleStore] Translations reloaded')
                    set({
                        forceRerender: get().forceRerender + 1,
                    })
                }
            )
            return {
                language,
                region,
            }
        },

        async loadLanguage(
            language: LanguageConfig | BasicLanguageConfig | LanguageCode | string
        ): Promise<void> {
            let languageConfig: LanguageConfig | null
            if (typeof language === 'string') {
                languageConfig = LocaleService.normalizeLanguage(language)
            } else {
                languageConfig = language as LanguageConfig
            }
            if (languageConfig) {
                await LocaleService.loadTranslations(languageConfig, true)
                set({
                    translationsLoaded: true,
                })
            }
            set({
                language: LocaleService.primaryLanguage,
            })
        },

        applyPreferredUserLanguage(language: LanguageCode): void {
            try {
                if (language && LocaleService.primaryLanguage.language !== language) {
                    // Предпочитаемый язык отличается от текущего.
                    const languages = LocaleService.getLanguagesListAsOptions()
                    for (const item of languages) {
                        const localeConfig: LanguageConfig = item.extra!
                        if (localeConfig.language === language) {
                            // Перезагружаем страницу с измененным языком.
                            const url = new URL(document.location.href)
                            url.searchParams.set(LocaleService.urlQueryArgName, localeConfig.language)
                            document.location.replace(url)
                            break
                        }
                    }
                }
            } catch (e) {
                // Игнорируем.
                console.error('[Auth][applyPreferredUserLanguage] Failed:', e)
            }
        },

        setRegion(
            region: RegionConfig | BasicRegionConfig | RegionCode | string
        ): void {
            let normalizedRegion: RegionConfig | null
            if (typeof region === 'string') {
                normalizedRegion = LocaleService.normalizeRegion(region)
            } else {
                normalizedRegion = region as RegionConfig
            }
            if (normalizedRegion) {
                LocaleService.setPrimaryRegion(normalizedRegion)
                setAppUrlsForRegion(LocaleService.primaryRegion.region)
            }
            set({
                region: LocaleService.primaryRegion,
            })
        },
    })
)
